/**
 * QuantityCalculatorCard — "how much do I need" turned into a number a
 * renovator will actually buy against (design import, spec §4.5, decision
 * D5, backend capability C3).
 *
 * PURE RENDERER. This component computes nothing it was not handed: the
 * backend's intelligence layer decides a turn warrants a calculation,
 * chooses one of the CLOSED formulas in `calculator-types.ts`, and supplies
 * every constant, unit, bound and label. No backend supplies a `calculator`
 * payload today (spec §D5), so the correct shipping behaviour is that this
 * renders **nothing** — that is the design working as specified, not a stub.
 *
 * Guard clauses enforce that nothing gets guessed:
 *   1. `spec == null` — no payload, nothing to show.
 *   2. `!isKnownFormula(spec.formula)` — a formula this client does not
 *      implement. Arithmetic that arrives as data is arithmetic nobody
 *      reviewed; an unrecognised name renders nothing rather than guessing.
 *   3. `!Number.isFinite(rawResult) || rawResult <= 0` — a formula missing
 *      one of its own required constants produces NaN/Infinity; every input
 *      is already clamped to the backend's own `min`, so a non-positive
 *      *finite* result past that clamp is not a legitimately-small job
 *      either — it is malformed constants (e.g. a zero/negative rate). Both
 *      cases get the same posture as an unknown formula: render nothing
 *      rather than fabricate a confident purchase count. (Every one of the
 *      three formulas already ends in `Math.ceil`, so a genuinely-tiny-but-
 *      positive job — e.g. 0.01 → 1 — never reaches this branch; only an
 *      exact zero or negative does, and per the reasoning above that is
 *      never a real "buy 1 anyway" situation.)
 *
 * Every label (`title`, `caption`, input `label`s, `result_label`) arrives
 * already localised from the backend and is rendered VERBATIM via
 * `pickLocalized` — never re-translated client-side. `result_unit` is a
 * required field on the wire (backend spec C3) but is deliberately NOT
 * appended as separate visible text next to the figure: `result_label`'s
 * own sentence already carries the unit in its correctly inflected form
 * ("26 мешков", not the bare singular "мешок" — RU/KY unit nouns decline by
 * count, and a bolted-on `result_unit` would either duplicate that word or
 * contradict its grammar). Instead it backs the figure's `aria-label`, so
 * assistive tech gets an explicit value+unit pairing without a sighted user
 * seeing the unit twice. The only client-owned VISIBLE copy is the
 * `calculator.costApprox` chrome around the priced total, which goes
 * through `react-i18next` like everything else this repo ships.
 *
 * Defensive decisions (documented for the reviewer; each has a covering
 * test as of the Task 4 review fix round):
 *   - A blank/invalid number field does not propagate NaN into the formula.
 *     `clampedNumber` falls back to the input's own `min` (or 0 if no min)
 *     before clamping, so a field the user is mid-typing never breaks the
 *     render.
 *   - Clamping happens on the VALUE FED TO THE FORMULA, not by rewriting
 *     the input's own text on every keystroke. Rewriting the displayed
 *     value on each `onChange` fights controlled-input typing (clearing a
 *     field then typing "0" would otherwise round-trip through a forced
 *     "1" before the "0" lands, corrupting what the user is typing). The
 *     bound is still fully honoured: every keystroke recomputes the result
 *     from the clamped number, so the figure the user sees never violates
 *     the backend's bounds even though the field's raw text can transiently
 *     read outside them.
 *   - A `select` whose `default` is not among its own `options` falls back
 *     to the first option rather than an unselectable value.
 *   - A NEW `spec` for the SAME mounted card instance re-seeds every input
 *     back to that spec's own `default`s (`seededFor`, compared by
 *     reference, reset during render — React's documented pattern for
 *     "adjusting state when a prop changes" without an effect, so the stale
 *     render never commits). This matters because the in-flight streaming
 *     turn's card is keyed by array INDEX
 *     (`MessageList.tsx`'s `key={\`local-${i}\`}`), not by spec identity, and
 *     capability C1 is explicitly an in-place update of the same line — so
 *     the same mounted instance really can receive a second, different
 *     `CalculatorSpec` (e.g. a `select`'s own `options` narrowing between
 *     payloads). Without this, the DOM would keep showing a `<select>`
 *     value that is no longer one of its own rendered `<option>`s while the
 *     figure quietly computed from the old number underneath it — a
 *     mismatch between the control and the figure on the one card whose
 *     entire purpose is letting the user check the figure against the
 *     control that produced it.
 *   - Cost renders only for a parsed, POSITIVE price. `parseDecimal`
 *     returns `0` for both "no price" and a malformed non-numeric string by
 *     design (see `lib/format.ts`) — those two must not be told apart by
 *     showing a confident "≈ 0 сом" for the malformed case.
 */
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { pickLocalized } from "@/lib/garden-api";
import { fmtNum, fmtSom, parseDecimal } from "@/lib/format";
import {
  FORMULAS,
  isKnownFormula,
  type CalculatorInput,
  type CalculatorSpec,
} from "@/features/chat/calculator-types";

export interface QuantityCalculatorCardProps {
  spec: CalculatorSpec | null;
}

/** Narrow the live i18n language to the ru/ky/en `pickLocalized`/`fmtSom` expect. */
function narrowLang(raw: string): "ru" | "ky" | "en" {
  const base = raw.split("-")[0] ?? "ru";
  return base === "ky" || base === "en" ? base : "ru";
}

/** The field's starting text: the backend's `default`, or — for a `select`
 * whose `default` isn't one of its own `options` — the first option, so the
 * control never opens on an unselectable value. */
function seedValue(input: CalculatorInput): string {
  if (input.kind === "select") {
    const options = input.options ?? [];
    const match = options.find((o) => o.value === String(input.default));
    if (match) return match.value;
    const first = options[0];
    if (first) return first.value;
  }
  return String(input.default);
}

/** Every input's starting text, keyed by `input.key`. `null`/an unknown
 * formula seeds an empty map — used both for the initial mount and for
 * re-seeding when a new `spec` arrives for the same mounted card (F4). */
function seedAll(spec: CalculatorSpec | null): Record<string, string> {
  if (spec == null) return {};
  return Object.fromEntries(spec.inputs.map((inp) => [inp.key, seedValue(inp)]));
}

/** Parse + clamp one field's raw text to the bounds the backend set, for use
 * in the formula. Never returns NaN: an empty/invalid field falls back to
 * the field's own `min` (or 0) before clamping, so a field mid-edit never
 * corrupts the computed result. */
function clampedNumber(raw: string, input: CalculatorInput): number {
  const parsed = Number.parseFloat(raw);
  let value = Number.isFinite(parsed) ? parsed : (input.min ?? 0);
  if (input.min != null) value = Math.max(input.min, value);
  if (input.max != null) value = Math.min(input.max, value);
  return value;
}

/** Split a backend `result_label` template on its one `{{n}}` token so the
 * number can render as its own styled figure (design §4.5: sentence text vs.
 * a bold primary-coloured figure). A template missing the token still
 * renders in full, with nothing to fill in — never throws. */
function splitOnToken(template: string): [string, string] {
  const idx = template.indexOf("{{n}}");
  if (idx === -1) return [template, ""];
  return [template.slice(0, idx), template.slice(idx + "{{n}}".length)];
}

export function QuantityCalculatorCard({
  spec,
}: QuantityCalculatorCardProps): ReactElement | null {
  const { t, i18n } = useTranslation("chat");
  const lang = narrowLang(i18n.language);
  const validSpec = spec != null && isKnownFormula(spec.formula) ? spec : null;

  // Lazy-seeded once per mount from the backend's `default`s. Hooks must run
  // unconditionally, so this tolerates `validSpec == null` (empty state) —
  // the actual bail-out happens below, after every hook has run.
  const [rawValues, setRawValues] = useState<Record<string, string>>(() =>
    seedAll(validSpec),
  );

  // F4 (Task 4 review): re-seed when the SAME mounted instance receives a
  // NEW spec — the in-flight turn's card is keyed by array index, not by
  // spec identity (see the module doc), and capability C1 is an in-place
  // update of the same line. Comparing `spec` by reference is deliberate: a
  // fresh backend payload is a fresh object even when a value is unchanged.
  // This is React's documented "adjust state during render" pattern — a
  // plain conditional `setState` while rendering, not an effect — so a
  // stale-input render never commits to the DOM, even for one frame.
  const [seededFor, setSeededFor] = useState<CalculatorSpec | null>(spec);
  if (spec !== seededFor) {
    setSeededFor(spec);
    setRawValues(seedAll(validSpec));
  }

  if (validSpec == null) return null;

  const numericValues: Record<string, number> = {};
  for (const input of validSpec.inputs) {
    numericValues[input.key] = clampedNumber(
      rawValues[input.key] ?? seedValue(input),
      input,
    );
  }

  const rawResult = FORMULAS[validSpec.formula](numericValues, validSpec.constants);
  // F9 (Task 4 review): a malformed payload (e.g. a formula missing one of
  // its own required constants) produces NaN/Infinity here — not a number
  // to show. Every input is already clamped to the backend's own `min`, so
  // a non-positive *finite* result past that clamp isn't a legitimately
  // tiny job either (every formula ends in `Math.ceil`, so a genuinely
  // small-but-positive job already rounds up to >= 1 before reaching this
  // line) — it is malformed constants. Both get the same "render nothing"
  // posture as an unknown formula. See the module doc for the full
  // reasoning and why this makes the floor below provably a no-op today
  // (kept anyway as defense-in-depth for a future formula that might not
  // end in `Math.ceil`).
  if (!Number.isFinite(rawResult) || rawResult <= 0) return null;
  const result = Math.max(1, rawResult);

  const title = pickLocalized(validSpec.title, lang);
  const caption = pickLocalized(validSpec.caption, lang);
  const resultLabel = pickLocalized(validSpec.result_label, lang);
  const resultUnit = pickLocalized(validSpec.result_unit, lang);
  const [before, after] = splitOnToken(resultLabel);

  // F7 (Task 4 review): `parseDecimal` returns 0 for BOTH "no price" and a
  // malformed non-numeric string (lib/format.ts's documented fallback) — a
  // confident "≈ 0 сом" must never render for either. Gate on a parsed,
  // POSITIVE price instead of merely non-null.
  const parsedPrice =
    validSpec.price_per_unit_kgs != null ? parseDecimal(validSpec.price_per_unit_kgs) : 0;
  const cost = parsedPrice > 0 ? result * parsedPrice : null;

  return (
    <div
      data-testid="quantity-calculator-card"
      className="flex flex-col gap-4 rounded-[18px] px-[22px] py-5"
      style={{
        backgroundColor: "var(--gd-surface)",
        border: "1px solid var(--gd-rule)",
      }}
    >
      <div className="flex flex-col gap-1">
        <div
          className="text-[17px] font-bold"
          style={{ color: "var(--gd-ink)", fontFamily: "var(--gd-display)" }}
        >
          {title}
        </div>
        <div
          data-testid="calc-caption"
          className="text-[11.5px] uppercase"
          style={{
            color: "var(--gd-ink-3)",
            fontFamily: "var(--gd-mono)",
            letterSpacing: "0.1em",
          }}
        >
          {caption}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {validSpec.inputs.map((input) => {
          const label = pickLocalized(input.label, lang);
          const unit = input.unit ? pickLocalized(input.unit, lang) : "";
          const value = rawValues[input.key] ?? seedValue(input);
          const testId = `calc-input-${input.key}`;

          return (
            <label key={input.key} className="flex w-[110px] flex-col gap-1">
              <span
                className="text-[12.5px]"
                style={{ color: "var(--gd-ink-3)", fontFamily: "var(--gd-text)" }}
              >
                {label}
                {unit ? ` (${unit})` : ""}
              </span>
              {input.kind === "select" ? (
                <select
                  data-testid={testId}
                  value={value}
                  onChange={(e) =>
                    setRawValues((prev) => ({ ...prev, [input.key]: e.target.value }))
                  }
                  className="rounded-[10px] px-[14px] py-[10px] text-[16px]"
                  style={{
                    border: "1px solid var(--gd-field-border)",
                    backgroundColor: "var(--gd-ground)",
                    fontFamily: "var(--gd-text)",
                    color: "var(--gd-ink-body)",
                  }}
                >
                  {(input.options ?? []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {pickLocalized(opt.label, lang)}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  data-testid={testId}
                  type="number"
                  value={value}
                  min={input.min ?? undefined}
                  max={input.max ?? undefined}
                  onChange={(e) =>
                    setRawValues((prev) => ({ ...prev, [input.key]: e.target.value }))
                  }
                  className="rounded-[10px] px-[14px] py-[10px] text-[16px]"
                  style={{
                    border: "1px solid var(--gd-field-border)",
                    backgroundColor: "var(--gd-ground)",
                    fontFamily: "var(--gd-text)",
                    color: "var(--gd-ink-body)",
                  }}
                />
              )}
            </label>
          );
        })}
      </div>

      <div
        className="flex flex-col gap-1 rounded-[12px] px-[18px] py-[12px]"
        style={{ backgroundColor: "var(--gd-wash)" }}
        aria-live="polite"
      >
        <div
          className="text-[15px]"
          style={{ color: "var(--gd-ink-2)", fontFamily: "var(--gd-text)" }}
        >
          {before}
          <strong
            data-testid="calc-result"
            aria-label={`${fmtNum(result)} ${resultUnit}`}
            className="text-[16px] font-bold"
            style={{ color: "var(--gd-primary)", fontFamily: "var(--gd-text)" }}
          >
            {fmtNum(result)}
          </strong>
          {after}
        </div>
        {cost != null ? (
          <div
            data-testid="calc-cost"
            className="text-[13.5px]"
            style={{ color: "var(--gd-ink-3)", fontFamily: "var(--gd-text)" }}
          >
            {t("calculator.costApprox", { cost: fmtSom(cost, lang) })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default QuantityCalculatorCard;
